import { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MembershipSource from 'apps/profile/sources/membership-source';
import AllUsersStore from 'apps/users/stores/all-users-store';
import FeatureStore from 'apps/layout/stores/feature-store';
import ProfileAssignmentStore from 'apps/profile/stores/profile-assignment-store';
import ProfileAssignmentSource from 'apps/profile/sources/profile-assignment-source';
import ListContactSource from 'apps/lists/sources/list-contact-source';
import TripContactsSource from 'apps/my-trips/sources/trip-contacts-source';
import MyTripUtils from 'apps/my-trips/utils/my-trips-utils';
import ProfileCardSubtitle from 'apps/profile/components/profile-card-subtitle';
import { Link, Icon, Loading, ModalConfirm, Button, Modal } from '@evertrue/et-components';
import { useFluxStore } from '@evertrue/et-flux';
import MembershipStore from 'apps/profile/stores/membership-store';
import { useIdentity } from 'base/identity-resolver';
import { useOpenClose } from '@evertrue/et-components';

// Store mapping functions
const membershipStoreMap = (contactId) => ({
  listMemberships: MembershipStore.getUnfilteredListMembership(contactId),
  tripMemberships: MembershipStore.getTripMembership(contactId),
  loadingMembership: MembershipStore.getLoading(),
});

const allUsersStoreMap = () => ({
  users: AllUsersStore.getAllUsers(),
});

const featureStoreMap = () => ({
  hasTrips: FeatureStore.hasFeature('my_trips'),
  hasVolunteers: FeatureStore.hasFeature('volunteers'),
  hasRmAssignments: FeatureStore.hasFeature('rm_assignments'),
});

const profileAssignmentStoreMap = (contactId) => ({
  volunteerAssignments: ProfileAssignmentStore.getVolunteerMembership(contactId),
  teamAssignments: ProfileAssignmentStore.getTeamMembership(contactId),
});

const Membership = ({ contact }) => {
  contact = contact || {};
  const [isModalOpen, openModal, closeModal] = useOpenClose();
  const { user } = useIdentity();

  // Use Flux stores
  const { listMemberships, tripMemberships, loadingMembership } = useFluxStore(MembershipStore, () =>
    membershipStoreMap(contact.id)
  );
  const { users } = useFluxStore(AllUsersStore, allUsersStoreMap);
  const { hasTrips, hasVolunteers, hasRmAssignments } = useFluxStore(FeatureStore, featureStoreMap);
  const { volunteerAssignments, teamAssignments } = useFluxStore(ProfileAssignmentStore, () =>
    profileAssignmentStoreMap(contact.id)
  );

  useEffect(() => {
    const fetchData = () => {
      if (contact.id) {
        MembershipSource.fetchListsForContact(contact.id);
        if (hasTrips) {
          MembershipSource.fetchTripsForContact(contact.id);
        }
        if (hasRmAssignments) {
          ProfileAssignmentSource.fetchTeamMembership(contact.id);
        }
        if (hasVolunteers) {
          ProfileAssignmentSource.fetchVolunteerMembership(contact.id);
        }
      }
    };

    fetchData();
  }, [contact.id, hasTrips, hasRmAssignments, hasVolunteers]);

  const handleRemove = (item) => {
    closeModal();
    switch (item.type) {
      case 'trip':
        TripContactsSource.removeContact(item.id, contact.id, { active_contact_id: contact.id });
        break;
      case 'list':
        ListContactSource.removeContacts(item.id, [contact.id], { active_contact_id: contact.id });
        break;
      default:
        break;
    }
  };

  const formattedMembershipSummary = () => {
    const firstName = contact.properties?.name_first?.value || 'Contact';
    const visibleListMemberships = listMemberships?.filter((l) => !l.hidden) || [];
    const listCount = visibleListMemberships.length;
    const tripCount = tripMemberships?.length || 0;
    const poolCount = volunteerAssignments?.length || 0;
    const teamCount = teamAssignments?.length || 0;

    let summary = `${firstName} is a member of ${listCount} ${listCount === 1 ? 'list' : 'lists'}`;
    if (hasTrips) {
      if (hasRmAssignments || hasVolunteers) {
        summary += ',';
      } else {
        summary += ' and';
      }
      summary += ` ${tripCount} ${tripCount === 1 ? 'trip' : 'trips'}`;
    }
    if (hasRmAssignments) {
      if (hasVolunteers) {
        summary += ',';
      } else {
        summary += ' and';
      }
      summary += ` ${teamCount} ${teamCount === 1 ? 'team' : 'teams'}`;
    }
    if (hasVolunteers) {
      summary += ` and ${poolCount} ${poolCount === 1 ? 'pool' : 'pools'}`;
    }
    return summary;
  };

  const formatLists = (lists = []) => {
    const mappedLists = lists.map((list) => {
      let icon = list.private ? 'lock' : 'group';
      if (list.name === 'Favorites' && list.user_id === user?.id) {
        icon = 'star';
      }
      return {
        ...list,
        icon,
        route: `/list/${list.id}`,
        type: 'list',
        is_editable: user.id === list.user_id,
      };
    });

    return mappedLists.sort((a, b) => a.name.localeCompare(b.name));
  };

  const formatTrips = (trips = []) => {
    const mappedTrips = trips.map((trip) => ({
      ...trip,
      icon: 'my-trips',
      name: `${trip.name} (${MyTripUtils.getTripDisplayDate(trip)})`,
      route: `/trips/${trip.id}`,
      type: 'trip',
      is_editable: user.id === trip.user_id,
    }));

    return mappedTrips.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Format memberships using the utility functions
  const formattedListMemberships = formatLists(listMemberships);
  const formattedTripMemberships = formatTrips(tripMemberships);

  const renderAllGroups = () => (
    <div className="membership--groups clearfix">
      {renderGroup('Lists', formattedListMemberships)}
      {hasTrips && renderGroup('Trips', formattedTripMemberships)}
      {hasVolunteers && renderGroup('Pools', volunteerAssignments)}
      {hasRmAssignments && renderGroup('Teams', teamAssignments)}
    </div>
  );

  const renderGroup = (label, items) => {
    items = items || [];
    const limit = items?.length || 0;
    return (
      <div className="membership--list">
        <ProfileCardSubtitle label={renderTitle(label, limit, items)} />
        {items.length === 0 ? (
          <div className="text-placeholder-light">
            Constituent has not been added to a {label.toLowerCase().slice(0, -1)}
          </div>
        ) : (
          <ul>
            {items
              .filter((item) => !item.hidden)
              .slice(0, limit)
              .map((item) => {
                const owner_name = users[item.user_id]?.name;
                const labels = item.labels || [];

                return (
                  <li key={item.id} className="membership--item clearfix">
                    <div className="membership--item-label clearfix">
                      <Link
                        title={item.name}
                        href={item.route}
                        data-refer="person_profile"
                        className="ellipsis-overflow"
                      >
                        {item.name}
                      </Link>
                      <div className="membership--owner">
                        {item.user_id === user.id
                          ? 'Owned by you'
                          : owner_name
                            ? `Owned by ${owner_name}`
                            : labels
                              ? labels.map((label, i) => (
                                  <div key={i}>
                                    {typeof label === 'object' ? label.value || JSON.stringify(label) : label}
                                  </div>
                                ))
                              : null}
                      </div>
                    </div>
                    {item.is_editable && <Icon icon="cancel" onClick={() => handleRemove(item)} />}
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    );
  };

  const renderTitle = (label, limit, items) => (
    <div className="membership--label">
      <span>{label}</span>
      {items?.length > limit && (
        <Button title="View Memberships" className="membership--title-more" onClick={openModal}>
          {` (+${items.length - limit} more)`}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div className={classNames('membership', { 'is-loading': loadingMembership })}>
        {loadingMembership ? (
          <Loading text="Loading..." />
        ) : (
          <div className="membership--summary">
            <div>{formattedMembershipSummary()}</div>
            <Button
              title="View Memberships"
              className="contact-profile--empty-button"
              type="secondary"
              onClick={openModal}
            >
              <Icon icon="check-circle" size={1} />
              View Memberships
            </Button>
          </div>
        )}
      </div>
      <Modal isOpen={isModalOpen} closeModal={closeModal} closeOnOutsideClick={true} size={'small'}>
        <ModalConfirm header="Membership" className="membership--view-all" onCancel={closeModal}>
          {renderAllGroups()}
        </ModalConfirm>
      </Modal>
    </>
  );
};

Membership.propTypes = {
  contact: PropTypes.object.isRequired,
};

export default Membership;
